import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { autorun } from 'mobx';
import * as Sentry from '@sentry/browser';

import User, { UserRole } from '../../../../Model/User';
import { RoleName } from '../../../../Model/Role';
import CoachPod from '../../../../Model/CoachPod';
import CoachNotificationView from '../../../../Model/CoachNotificationView';
import InternalAssignment from '../../../../Model/InternalAssignment';
import { AssignmentRole } from '../../../../utils/internalAssignments';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import { buildACQueueEntries } from '../utils/acLeadUtils';

/**
 * A task can breach its SLA without any document changing, so the rows are also rebuilt on a
 * timer to keep "overdue by" and the row set current.
 */
const OVERDUE_REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Streams every AC's queues.
 *
 * Both underlying collections are live Firestore subscriptions, so the rows reflect the current
 * state without a manual refresh.
 *
 * @returns {object} { queueEntries, acAssignments, selectableACNamesById, hasLoadedDecorations, isReady }
 */
const useACLeadData = () => {
  const [coachViewsCollection, setCoachViewsCollection] = useState(null);
  const [acAssignmentsCollection, setACAssignmentsCollection] = useState(null);
  const [acSlackIdById, setACSlackIdById] = useState(new Map());
  const [selectableACNamesById, setSelectableACNamesById] = useState(new Map());
  const [queueEntries, setQueueEntries] = useState([]);
  const [acAssignments, setACAssignments] = useState([]);
  const [podNamesById, setPodNamesById] = useState(new Map());
  /*
   * Whether the decorations pass has settled, either way. The AC filter waits on this rather than on
   * its options alone: the roster and the decorations are loaded independently, so an options list
   * built before this is true is the unnarrowed fallback, and MultiSelectFilter resolves its stored
   * selection against whatever list it first mounts with — see `ACLeadDashboard`.
   */
  const [hasLoadedDecorations, setHasLoadedDecorations] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    /*
     * Slack ids, AC names and pod names decorate the rows; the roster itself does not depend on
     * them. Loaded in their own pass so that losing them costs the chase button, the AC filter's
     * narrowing and the pod labels rather than the whole page — an AC Lead granted only this page
     * need not have read access to the user collection, and `Promise.all` would have discarded the
     * roster alongside it.
     */
    const loadRowDecorations = async () => {
      try {
        const [acUsersCol, podsCol] = await Promise.all([
          /*
           * The assignment docs carry the AC's name but not their Slack id, which the chase action
           * needs and which lives on the AC's own user document.
           *
           * Queried by the AC role flag, not User.getInternalUsers(): that one selects on the
           * `admin` user claim, which an AC need not hold — one who does not would silently lose
           * their chase button. Coach.getAssistantCoaches() is avoided for a similar reason, as it
           * additionally requires `flags.isActive`.
           *
           * This read is also what narrows the AC filter to the people who actually hold the role,
           * and what gives it their full names — an assignment carries a first name only, and
           * there is more than one Felipe and more than one Nicole.
           */
          User.getUsersByRole(UserRole.AC),
          // Rows are labelled with the pod they came from, which the assignment only holds by id.
          CoachPod.getAllPods(),
        ]);

        if (isComponentMountedRef.current) {
          setACSlackIdById(new Map(acUsersCol.docs.map(({ id, slackId }) => [id, slackId])));
          /*
           * The filter offers the ACs a lead chases, so the leads themselves are left out of it —
           * they hold the AC role too, and their own rows are not work anyone is chasing them for.
           * Dropped by role rather than by name so the list does not go stale with the next
           * staffing change. Their rows stay in the table: those assignments are the AC of record
           * for real coaches, and hiding them would hide real backlog.
           *
           * Trimmed because `User.name` joins first and last unconditionally, so someone with no
           * surname arrives with a trailing space. An empty name is kept as a key regardless: the
           * key is what says this person belongs in the filter, and buildACFilterOptions falls
           * back to the assignment's own name for the label.
           */
          setSelectableACNamesById(new Map(
            acUsersCol.docs
              .filter(({ roles }) => !roles.includes(RoleName.AC_LEAD))
              .map(({ id, name }) => [id, (name ?? '').trim()]),
          ));
          setPodNamesById(new Map(podsCol.docs.map((pod) => [pod.id, pod.name ?? ''])));
        }
      } catch (error) {
        // The rows still stand without these, so this is recorded and nothing else changes.
        Sentry.captureException(error);
      } finally {
        /*
         * Released on the failure path too. A read that failed is settled — the filter should fall
         * back to every assignment and open, rather than never appearing at all.
         */
        if (isComponentMountedRef.current) {
          setHasLoadedDecorations(true);
        }
      }
    };

    const init = async () => {
      // Kicked off first so it overlaps the roster read rather than following it.
      const decorationsLoaded = loadRowDecorations();

      try {
        const [coachViewsCol, acAssignmentsCol] = await Promise.all([
          CoachNotificationView.getAllExternalACCoachNotificationViews(),
          InternalAssignment.getAllCurrentAssignmentsByRole(AssignmentRole.ASSISTANT_COACH),
        ]);

        if (isComponentMountedRef.current) {
          setCoachViewsCollection(coachViewsCol);
          setACAssignmentsCollection(acAssignmentsCol);
        }
      } catch (error) {
        /*
         * These two are the roster, so without them there are no rows to draw. Nothing below sets
         * `isReady` when they never arrive — `rebuildRows` returns at its first guard — so the page
         * would hold its loading overlay indefinitely. Release it here so the lead lands on the
         * empty-rows overlay, which is at least a state they can report, and record why.
         */
        Sentry.captureException(error);

        if (isComponentMountedRef.current) {
          setIsReady(true);
        }
      }

      await decorationsLoaded;
    };

    init();
  }, [isComponentMountedRef]);

  const rebuildRows = useCallback(() => {
    if (!coachViewsCollection || !acAssignmentsCollection) {
      return;
    }

    const assignments = [...acAssignmentsCollection.docs];
    const entries = buildACQueueEntries(
      [...coachViewsCollection.docs],
      assignments,
      acSlackIdById,
      podNamesById,
    );

    if (isComponentMountedRef.current) {
      setQueueEntries(entries);
      /*
       * Held to the same array while the underlying documents are unchanged. The rows are rebuilt
       * on a timer to keep ages current, and handing back a new array each time would rebuild the
       * AC filter's option objects with it. MUI's multi-select compares object values by reference,
       * so a selected AC would stop matching its own menu entry after the first refresh and
       * clicking it again would re-add it rather than clear it — a filter that cannot be undone.
       */
      setACAssignments((current) => (
        current.length === assignments.length && current.every((doc, index) => doc === assignments[index])
          ? current
          : assignments
      ));
      setIsReady(true);
    }
  }, [
    coachViewsCollection,
    acAssignmentsCollection,
    acSlackIdById,
    podNamesById,
    isComponentMountedRef,
  ]);

  // Rebuild whenever either collection receives an update.
  useEffect(() => autorun(rebuildRows), [rebuildRows]);

  useEffect(() => {
    const intervalId = setInterval(rebuildRows, OVERDUE_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [rebuildRows]);

  return {
    queueEntries,
    acAssignments,
    selectableACNamesById,
    hasLoadedDecorations,
    isReady,
  };
};

export default useACLeadData;
